import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, ExternalLink, X, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { findFallbackCase } from "@/data/marketing";

function Lightbox({ images, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose, onPrev, onNext]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-8" data-testid="lightbox">
      <button onClick={onClose} data-testid="lightbox-close"
        className="absolute top-6 right-6 w-11 h-11 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors">
        <X size={18}/>
      </button>
      <button onClick={onPrev} data-testid="lightbox-prev"
        className="absolute left-4 md:left-8 w-11 h-11 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors">
        <ChevronLeft size={20}/>
      </button>
      <button onClick={onNext} data-testid="lightbox-next"
        className="absolute right-4 md:right-8 w-11 h-11 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors">
        <ChevronRight size={20}/>
      </button>
      <AnimatePresence mode="wait">
        <motion.img
          key={index}
          src={images[index]}
          alt={`Frame ${index+1}`}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          className="max-h-[80vh] max-w-[92vw] sm:max-h-[85vh] object-contain rounded-xl shadow-[0_40px_120px_rgba(139,92,246,0.35)]"
        />
      </AnimatePresence>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 label-mono" data-testid="lightbox-counter">
        {String(index+1).padStart(2,"0")} / {String(images.length).padStart(2,"0")}
      </div>
    </div>
  );
}

export default function CaseStudy() {
  const { itemId } = useParams();
  const nav = useNavigate();
  const fallbackItem = findFallbackCase(itemId);
  const [item, setItem] = useState(fallbackItem || null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get(`/portfolio/${itemId}`).then(r => setItem(r.data)).catch(() => setItem(fallbackItem || false));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [itemId, fallbackItem]);

  if (item === false) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="case-not-found">
      <div className="label-mono mb-3">404</div>
      <h1 className="font-display font-black text-4xl">Case study not found.</h1>
      <Link to="/portfolio" className="pill-btn primary mt-8 inline-flex">Back to work</Link>
    </div>
  );
  if (!item) return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="case-loading">
      <div className="glass rounded-xl px-6 py-4 text-white/60 text-sm">Loading case study…</div>
    </div>
  );

  const gallery = item.gallery && item.gallery.length ? item.gallery : [item.cover];
  const meta = [
    { label: "Client", value: item.client || item.industry },
    { label: "Role", value: item.role || "Design + Build" },
    { label: "Engagement", value: item.duration || "Project" },
  ];

  return (
    <div data-testid="case-study-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/[0.06]">
        <div className="absolute inset-0 opacity-40">
          <img src={item.cover} alt={item.title} className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/60 to-[#05050A]"/>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-12 sm:pt-16 md:pt-20 pb-16 sm:pb-20 md:pb-24">
          <button onClick={() => nav(-1)} className="label-mono inline-flex items-center gap-2 hover:text-white transition-colors" data-testid="case-back">
            <ArrowLeft size={14}/> Back
          </button>
          <div className="mt-8 flex flex-wrap items-center gap-2">
            <span className="chip">{item.category.replace(/-/g, " ")}</span>
            <span className="chip cyan">{item.style}</span>
            <span className="label-mono ml-2">{item.industry}</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-display font-black text-3xl sm:text-4xl md:text-5xl lg:text-7xl mt-6 leading-[0.92] max-w-full sm:max-w-4xl md:max-w-5xl">
            {item.title}
          </motion.h1>
          <p className="mt-4 sm:mt-6 text-white/70 text-base sm:text-lg max-w-full sm:max-w-2xl leading-relaxed">{item.summary}</p>

          <div className="mt-10 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 max-w-full sm:max-w-3xl">
            {meta.map((m, i) => (
              <div key={i} data-testid={`case-meta-${m.label.toLowerCase()}`}>
                <div className="label-mono mb-2">{m.label}</div>
                <div className="font-display font-bold text-base sm:text-lg">{m.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      {item.problem && (
        <section className="max-w-7xl mx-auto px-6 py-16 sm:py-20" data-testid="case-problem">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="label-mono mb-3">01 · The problem</div>
              <h2 className="font-display font-black text-2xl sm:text-3xl md:text-4xl leading-tight">Where the gap was.</h2>
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <p className="text-base sm:text-lg md:text-xl text-white/80 leading-relaxed font-light">{item.problem}</p>
            </div>
          </div>
        </section>
      )}

      {/* PROCESS */}
      {Array.isArray(item.process) && item.process.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16 sm:py-20 border-t border-white/[0.06]" data-testid="case-process">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-12 sm:mb-14">
            <div>
              <div className="label-mono mb-3">02 · Process</div>
              <h2 className="font-display font-black text-2xl sm:text-3xl md:text-4xl">How we got there.</h2>
            </div>
            <div className="text-white/50 text-sm max-w-md">Every step below was a real working phase — no theatre, no filler decks.</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {item.process.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.08 }}
                className="p-6 sm:p-8 rounded-2xl tracing-beam"
                data-testid={`process-step-${i+1}`}>
                <div className="flex items-start gap-4 sm:gap-5">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-400 flex items-center justify-center font-display font-black text-black text-lg shrink-0">
                    {String(i+1).padStart(2, "0")}
                  </div>
                  <div>
                    <div className="font-display font-bold text-lg sm:text-2xl">{p.title}</div>
                    <div className="text-white/60 mt-2 leading-relaxed">{p.detail}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* RESULTS */}
      {Array.isArray(item.results) && item.results.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16 sm:py-20 border-t border-white/[0.06]" data-testid="case-results">
          <div className="label-mono mb-3">03 · Results</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl mb-12">Numbers that stuck.</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {item.results.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="p-4 sm:p-6 md:p-8 rounded-2xl hairline bg-[#0A0A10]"
                data-testid={`result-metric-${i}`}>
                <div className="font-display font-black text-3xl sm:text-4xl md:text-5xl bg-gradient-to-r from-violet-300 to-cyan-300 bg-clip-text text-transparent">
                  {r.metric}
                </div>
                <div className="text-white/60 text-sm mt-3 leading-snug">{r.label}</div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* GALLERY / LIGHTBOX REEL */}
      <section className="max-w-7xl mx-auto px-6 py-16 sm:py-20 border-t border-white/[0.06]" data-testid="case-gallery">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <div className="label-mono mb-3">04 · The reel</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl">See it up close.</h2>
          </div>
          <div className="label-mono">{gallery.length} frames · click to expand</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-[160px] md:auto-rows-[260px]">
          {gallery.map((src, i) => {
            // Asymmetric bento tile spans
            const spans = ["md:col-span-8 md:row-span-2", "md:col-span-4", "md:col-span-4", "md:col-span-8", "md:col-span-6", "md:col-span-6"];
            return (
              <button
                key={i}
                onClick={() => setLightbox(i)}
                data-testid={`gallery-tile-${i}`}
                className={`${spans[i % spans.length]} group relative overflow-hidden rounded-2xl hairline`}>
                <img src={src} alt={`${item.title} frame ${i+1}`} loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"/>
                <div className="absolute bottom-3 left-3 label-mono opacity-0 group-hover:opacity-100 transition-opacity">
                  Frame {String(i+1).padStart(2,"0")}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIAL */}
      {item.testimonial?.quote && (
        <section className="max-w-5xl mx-auto px-6 py-16 sm:py-20" data-testid="case-testimonial">
          <div className="glass rounded-3xl p-8 sm:p-10 md:p-14 relative overflow-hidden">
            <Quote size={40} className="text-violet-400/40"/>
            <blockquote className="mt-4 font-display font-light text-2xl sm:text-3xl md:text-4xl leading-[1.15] text-white/90 italic">
              "{item.testimonial.quote}"
            </blockquote>
            <div className="mt-6 sm:mt-8 label-mono">— {item.testimonial.author}</div>
          </div>
        </section>
      )}

      {/* CTA + RELATED */}
      <section className="max-w-7xl mx-auto px-6 py-16 sm:py-20 border-t border-white/[0.06]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
          <div>
            <div className="label-mono mb-3">05 · Next</div>
            <h3 className="font-display font-black text-3xl sm:text-4xl md:text-5xl leading-tight">Build the next result with us.</h3>
            <div className="mt-6 sm:mt-8 flex flex-wrap gap-3">
              <Link to="/order" className="pill-btn violet" data-testid="case-cta-order">
                Start your project <ArrowUpRight size={16}/>
              </Link>
              <Link to="/booking" className="pill-btn ghost" data-testid="case-cta-book">
                Book a call
              </Link>
              {item.demo && item.demo !== "#" && (
                <a href={item.demo} target="_blank" rel="noreferrer" className="pill-btn ghost" data-testid="case-cta-demo">
                  Visit live <ExternalLink size={14}/>
                </a>
              )}
            </div>
          </div>
          <div className="space-y-3">
            <div className="label-mono">More work</div>
            {(item.related || []).map(r => (
              <Link key={r.item_id} to={`/portfolio/${r.item_id}`}
                data-testid={`related-${r.item_id}`}
                className="flex items-center gap-4 hairline rounded-xl p-3 hover:border-white/25 transition-colors">
                <img src={r.cover} alt={r.title} className="w-20 h-16 object-cover rounded-lg"/>
                <div className="flex-1">
                  <div className="font-display font-bold">{r.title}</div>
                  <div className="text-xs text-white/50">{r.category.replace(/-/g, " ")} · {r.style}</div>
                </div>
                <ArrowRight size={16} className="text-white/40"/>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {lightbox !== null && (
        <Lightbox
          images={gallery}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onPrev={() => setLightbox((i) => (i - 1 + gallery.length) % gallery.length)}
          onNext={() => setLightbox((i) => (i + 1) % gallery.length)}
        />
      )}
    </div>
  );
}
